import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { DateFilter, DateRange } from '../components/DateFilter';
import {
  AlertTriangle,
  Shield,
  Calendar,
  User,
  ExternalLink,
  Clock,
  RefreshCw,
  Filter,
  Search,
  ChevronDown,
  Eye,
  Target,
  Brain,
  Zap,
  CheckCircle,
  Activity,
  FileText,
  Maximize2,
  Image as ImageIcon,
  Globe,
  Bell,
  TrendingUp,
  XCircle,
  Users,
  BarChart3,
  PieChart as PieChartIcon,
  Hash,
  AlertOctagon,
  Database,
  Sparkles,
  Rss,
  Settings,
  ChevronUp,
  Tag
} from 'lucide-react';
import { formatSafeRelativeTime } from '../utils/date-formatter';
import { SourceBadge } from '../components/SourceBadge';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatPostIdForDisplay } from '../utils/id-normalization';
import { Pagination } from '../components/Pagination';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  Area,
  AreaChart,
  ComposedChart,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Treemap
} from 'recharts';

interface AnalyzedPost {
  id: string;
  analysis_id?: string;
  username: string;
  content: string;
  created_at: string;
  updated_at: string;
  url: string;
  source_feed: string;
  threat_level: string;
  threat_types?: string;
  confidence?: number;
  summary?: string;
  ttps?: string[];
  iocs?: string[];
  recommendations?: string[];
  analyzed_at?: string;
  has_images?: boolean;
  has_website_data?: boolean;
  website_crawls_count?: number;
  alert_count?: number;
  latest_alert_date?: string;
  latest_alert_id?: string;
  analyzed_steps?: {
    topic_relevance: number;
    threat_assessment: string;
    confidence_score: number;
    ai_model_used: string;
    analysis_timestamp: string;
  };
  // AI Provider tracking fields
  ai_provider?: string;
  ai_model?: string;
  ai_response_metadata?: string;
  ai_analysis?: {
    ai_provider?: string;
    ai_model?: string;
    ai_response_metadata?: any;
  };
  enhanced_analysis?: {
    ai_provider?: string;
    ai_model?: string;
    ai_response_metadata?: any;
  };
}

interface PostsResponse {
  posts: AnalyzedPost[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const THREAT_COLORS = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-green-100 text-green-800 border-green-200',
  unknown: 'bg-gray-100 text-gray-800 border-gray-200'
};

const CHART_COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e',
  unknown: '#6b7280'
};

const THREAT_ICONS = {
  critical: AlertTriangle,
  high: AlertTriangle,
  medium: Shield,
  low: Shield,
  unknown: Shield
};

// RSS.app Feeds Searchable Selector Component
interface RSSAppFeedsSelectorProps {
  feeds: Array<{
    id: string;
    source_feed: string;
    post_count: number;
    name: string;
    url?: string;
    description?: string;
    last_synced?: string | null;
  }>;
  selectedSources: string[];
  onSourceToggle: (source: string) => void;
}

function RSSAppFeedsSelector({ feeds, selectedSources, onSourceToggle }: RSSAppFeedsSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFeeds, setFilteredFeeds] = useState(feeds);

  // Filter feeds based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredFeeds(feeds);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = feeds.filter(feed =>
        (feed.name || feed.id).toLowerCase().includes(query) ||
        feed.source_feed.toLowerCase().includes(query)
      );
      setFilteredFeeds(filtered);
    }
  }, [searchQuery, feeds]);

  const selectedFeedsCount = selectedSources.filter(source =>
    feeds.some(feed => feed.source_feed === source)
  ).length;

  return (
    <div className="space-y-3">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Search RSS.app feeds..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <XCircle className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Selected Count and Toggle All */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">
          {selectedFeedsCount > 0 ? (
            <span className="text-blue-600 font-medium">
              {selectedFeedsCount} of {feeds.length} feeds selected
            </span>
          ) : (
            <span>{feeds.length} RSS.app feeds available</span>
          )}
        </span>
        <div className="flex space-x-2">
          {selectedFeedsCount > 0 && (
            <button
              onClick={() => {
                // Remove all RSS.app feed selections
                const rssAppSources = feeds.map(f => f.source_feed);
                rssAppSources.forEach(source => {
                  if (selectedSources.includes(source)) {
                    onSourceToggle(source);
                  }
                });
              }}
              className="text-xs text-red-600 hover:text-red-800"
            >
              Clear All
            </button>
          )}
          <button
            onClick={() => {
              // Select all filtered feeds
              filteredFeeds.forEach(feed => {
                if (!selectedSources.includes(feed.source_feed)) {
                  onSourceToggle(feed.source_feed);
                }
              });
            }}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Select All Visible
          </button>
        </div>
      </div>

      {/* Feeds List */}
      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
        {filteredFeeds.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            {searchQuery ? `No feeds found matching "${searchQuery}"` : 'No RSS.app feeds found'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredFeeds.map(feed => (
              <label
                key={feed.source_feed}
                className="flex items-center space-x-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedSources.includes(feed.source_feed)}
                  onChange={() => onSourceToggle(feed.source_feed)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full font-medium">
                      RSS.app
                    </span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {feed.post_count} posts
                    </span>
                  </div>
                  <div className="mt-1">
                    <span className="text-sm font-medium text-gray-900 truncate block" title={feed.name || feed.id}>
                      {feed.name || feed.id}
                    </span>
                    {feed.last_synced && (
                      <span className="text-xs text-gray-500">
                        Last: {formatSafeRelativeTime(feed.last_synced, { addSuffix: true })}
                      </span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Show search stats */}
      {searchQuery && (
        <div className="text-xs text-gray-500 text-center">
          Showing {filteredFeeds.length} of {feeds.length} feeds
        </div>
      )}
    </div>
  );
}

// RSS Feeds Searchable Selector Component
interface RSSFeedsSelectorProps {
  feeds: Array<{
    id: string;
    source_feed: string;
    post_count: number;
    name: string;
    url?: string;
    description?: string;
    last_synced?: string | null;
  }>;
  selectedSources: string[];
  onSourceToggle: (source: string) => void;
}

function RSSFeedsSelector({ feeds, selectedSources, onSourceToggle }: RSSFeedsSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFeeds, setFilteredFeeds] = useState(feeds);

  // Filter feeds based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredFeeds(feeds);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = feeds.filter(feed =>
        (feed.name || feed.id).toLowerCase().includes(query) ||
        feed.source_feed.toLowerCase().includes(query)
      );
      setFilteredFeeds(filtered);
    }
  }, [searchQuery, feeds]);

  const selectedFeedsCount = selectedSources.filter(source =>
    feeds.some(feed => feed.source_feed === source)
  ).length;

  return (
    <div className="space-y-3">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Search RSS feeds..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Bulk Actions */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">
          {selectedFeedsCount} of {feeds.length} feeds selected
        </span>
        <div className="space-x-2">
          <button
            onClick={() => feeds.forEach(feed => {
              if (!selectedSources.includes(feed.source_feed)) {
                onSourceToggle(feed.source_feed);
              }
            })}
            className="text-blue-600 hover:text-blue-800"
          >
            Select All Visible
          </button>
          <button
            onClick={() => feeds.forEach(feed => {
              if (selectedSources.includes(feed.source_feed)) {
                onSourceToggle(feed.source_feed);
              }
            })}
            className="text-red-600 hover:text-red-800"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Feeds List */}
      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
        {filteredFeeds.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            {searchQuery ? `No feeds found matching "${searchQuery}"` : 'No RSS feeds found'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredFeeds.map(feed => (
              <label
                key={feed.source_feed}
                className="flex items-center space-x-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedSources.includes(feed.source_feed)}
                  onChange={() => onSourceToggle(feed.source_feed)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full font-medium">
                      RSS
                    </span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {feed.post_count} posts
                    </span>
                  </div>
                  <div className="mt-1">
                    <span className="text-sm font-medium text-gray-900 truncate block" title={feed.name || feed.id}>
                      {feed.name || feed.id}
                    </span>
                    {feed.last_synced && (
                      <span className="text-xs text-gray-500">
                        Last: {formatSafeRelativeTime(feed.last_synced, { addSuffix: true })}
                      </span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Show search stats */}
      {searchQuery && (
        <div className="text-xs text-gray-500 text-center">
          Showing {filteredFeeds.length} of {feeds.length} feeds
        </div>
      )}
    </div>
  );
}

// Threat Types Searchable Selector Component
interface ThreatTypesSelectorProps {
  threatTypes: Array<{
    name: string;
    display_name: string;
    count: number;
  }>;
  selectedThreatTypes: string[];
  onThreatTypeToggle: (threatType: string) => void;
}

function ThreatTypesSelector({ threatTypes, selectedThreatTypes, onThreatTypeToggle }: ThreatTypesSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredThreatTypes, setFilteredThreatTypes] = useState(threatTypes);

  // Filter threat types based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredThreatTypes(threatTypes);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = threatTypes.filter(type =>
        type.display_name.toLowerCase().includes(query) ||
        type.name.toLowerCase().includes(query)
      );
      setFilteredThreatTypes(filtered);
    }
  }, [searchQuery, threatTypes]);

  const selectedThreatTypesCount = selectedThreatTypes.filter(threatType =>
    threatTypes.some(type => type.name === threatType)
  ).length;

  return (
    <div className="space-y-3">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Search threat types..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <XCircle className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Selected Count and Toggle All */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">
          {selectedThreatTypesCount > 0 ? (
            <span className="text-red-600 font-medium">
              {selectedThreatTypesCount} of {threatTypes.length} threat types selected
            </span>
          ) : (
            <span>{threatTypes.length} threat types available</span>
          )}
        </span>
        <div className="flex space-x-2">
          {selectedThreatTypesCount > 0 && (
            <button
              onClick={() => {
                // Remove all threat type selections
                selectedThreatTypes.forEach(threatType => {
                  onThreatTypeToggle(threatType);
                });
              }}
              className="text-xs text-red-600 hover:text-red-800"
            >
              Clear All
            </button>
          )}
          <button
            onClick={() => {
              // Select all filtered threat types
              filteredThreatTypes.forEach(type => {
                if (!selectedThreatTypes.includes(type.name)) {
                  onThreatTypeToggle(type.name);
                }
              });
            }}
            className="text-xs text-red-600 hover:text-red-800"
          >
            Select All Visible
          </button>
        </div>
      </div>

      {/* Threat Types List */}
      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
        {filteredThreatTypes.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            {searchQuery ? `No threat types found matching "${searchQuery}"` : 'No threat types found'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredThreatTypes.map(threatType => (
              <label
                key={threatType.name}
                className="flex items-center space-x-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedThreatTypes.includes(threatType.name)}
                  onChange={() => onThreatTypeToggle(threatType.name)}
                  className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded-full font-medium">
                      Threat
                    </span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {threatType.count} posts
                    </span>
                  </div>
                  <div className="mt-1">
                    <span className="text-sm font-medium text-gray-900 truncate block" title={threatType.display_name}>
                      {threatType.display_name}
                    </span>
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Show search stats */}
      {searchQuery && (
        <div className="text-xs text-gray-500 text-center">
          Showing {filteredThreatTypes.length} of {threatTypes.length} threat types
        </div>
      )}
    </div>
  );
}

// CACHE BUST v3.0 - 2025-06-28-22:00 - Fixed count and date display
export function AnalyzedPosts() {
  const [posts, setPosts] = useState<AnalyzedPost[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedPost, setExpandedPost] = useState<string | null>(null);
  const [analyzedCount, setAnalyzedCount] = useState(0);
  const [showDashboard, setShowDashboard] = useState(true);
  const [metrics, setMetrics] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Filters
  const [threatLevel, setThreatLevel] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAnalyzedOnly, setShowAnalyzedOnly] = useState(false);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [feedType, setFeedType] = useState('');
  const [selectedThreatTypes, setSelectedThreatTypes] = useState<string[]>([]);
  const [availableSources, setAvailableSources] = useState<string[]>([]);
  const [availableThreatTypes, setAvailableThreatTypes] = useState<Array<{
    name: string;
    display_name: string;
    count: number;
  }>>([]);
  const [analyzedStats, setAnalyzedStats] = useState<any>(null);
  const [rssAppFeeds, setRssAppFeeds] = useState<Array<{
    id: string;
    source_feed: string;
    post_count: number;
    name: string;
    url: string;
    description: string;
    last_synced: string | null;
  }>>([]);
  const [rssFeeds, setRssFeeds] = useState<Array<{
    id: string;
    source_feed: string;
    post_count: number;
    name: string;
    url: string;
    description: string;
    last_synced: string | null;
  }>>([]);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
    period: 'all'
  });
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);

  // Navigate to post details when a post is selected
  useEffect(() => {
    if (selectedPostId) {
      navigate(`/posts/${selectedPostId}`);
    }
  }, [selectedPostId, navigate]);

  useEffect(() => {
    loadPosts();
    loadDashboardData();
    loadAnalyzedStats();
  }, [pagination.page, threatLevel, showAnalyzedOnly, selectedSources, feedType, selectedThreatTypes, dateRange]);

  // Load available sources from current posts
  useEffect(() => {
    if (posts.length > 0) {
      const sources = Array.from(new Set(posts.map(post => post.source_feed))).sort();
      setAvailableSources(sources);
    }
  }, [posts]);

  // Load RSS.app feeds, RSS feeds, and threat types on component mount
  useEffect(() => {
    const loadDynamicData = async () => {
      try {
        // Load RSS.app feeds
        const feedsResponse = await api.getRssAppFeeds();
        if (feedsResponse.feeds) {
          setRssAppFeeds(feedsResponse.feeds);
        }

        // Load RSS feeds
        const rssFeedsResponse = await api.getRssFeeds();
        if (rssFeedsResponse.feeds) {
          setRssFeeds(rssFeedsResponse.feeds);
        }

        // Load threat types
        const typesResponse = await api.getThreatTypes();
        if (typesResponse.threat_types) {
          setAvailableThreatTypes(typesResponse.threat_types);
        }
      } catch (error) {
        console.error('Error loading dynamic data:', error);
      }
    };

    loadDynamicData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Prepare date parameters
      const dateParams: any = {};
      if (dateRange.startDate) {
        dateParams.from_date = dateRange.startDate.toISOString();
      }
      if (dateRange.endDate) {
        dateParams.to_date = dateRange.endDate.toISOString();
      }

      // Calculate stats range based on date selection
      let statsRange = '7d';
      if (dateRange.period === '7days') {
        statsRange = '7d';
      } else if (dateRange.period === '30days') {
        statsRange = '30d';
      } else if (dateRange.period === 'all') {
        statsRange = 'all';
      }

      // For "all time", don't send date parameters
      const apiDateParams = dateRange.period === 'all' ? {} : dateParams;

      const [metricsData, statsData] = await Promise.all([
        api.getMetrics(apiDateParams),
        api.getStats(statsRange, apiDateParams)
      ]);

      setMetrics(metricsData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    }
  };

  const loadAnalyzedStats = async () => {
    try {
      // Prepare date parameters
      const dateParams: any = {};
      if (dateRange.startDate) {
        dateParams.from_date = dateRange.startDate.toISOString();
      }
      if (dateRange.endDate) {
        dateParams.to_date = dateRange.endDate.toISOString();
      }

      // Calculate stats range based on date selection
      let statsRange = 'all';
      if (dateRange.period === '7days') {
        statsRange = '7d';
      } else if (dateRange.period === '30days') {
        statsRange = '30d';
      }

      // For "all time", don't send date parameters
      const apiDateParams = dateRange.period === 'all' ? {} : dateParams;

      // Load stats for analyzed posts to get accurate threat counts
      const analyzedStatsData = await api.getStats(statsRange, apiDateParams);
      setAnalyzedStats(analyzedStatsData);
    } catch (error) {
      console.error('Failed to load analyzed stats:', error);
    }
  };

  // Handle highlighting from URL parameters
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId) {
      setExpandedPost(highlightId);
      // Scroll to the highlighted post after a short delay
      setTimeout(() => {
        const element = document.getElementById(`post-${highlightId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }, [searchParams, posts]);

  const loadPosts = async () => {
    try {
      setRefreshing(true);

      // Prepare date parameters
      const dateParams: any = {};
      if (dateRange.startDate) {
        dateParams.from_date = dateRange.startDate.toISOString();
      }
      if (dateRange.endDate) {
        dateParams.to_date = dateRange.endDate.toISOString();
      }

      // For "all time", don't send date parameters
      const apiDateParams = dateRange.period === 'all' ? {} : dateParams;

      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        analyzed: 'true',
        order_by: 'analyzed_at',
        ...apiDateParams
      };

      console.log('[ANALYZED-POSTS] API params:', params);

      if (threatLevel) params.threat_level = threatLevel;
      if (selectedSources.length > 0) params.source_feed = selectedSources.join(',');
      if (feedType) params.feed_type = feedType;
      if (selectedThreatTypes.length > 0) params.threat_type = selectedThreatTypes.join(',');

      const response = await api.getPosts(params);

      console.log('[ANALYZED-POSTS] API response:', {
        postsCount: response.posts.length,
        pagination: response.pagination,
        firstPost: response.posts[0] ? {
          username: response.posts[0].username,
          analyzed_at: response.posts[0].analyzed_at,
          threat_level: response.posts[0].threat_level,
          source_feed: response.posts[0].source_feed
        } : null
      });

      // Log first 5 posts with username 'unknown'
      const unknownUserPosts = response.posts.filter(p => p.username === 'unknown').slice(0, 5);
      if (unknownUserPosts.length > 0) {
        console.log('[ANALYZED-POSTS] Posts with username "unknown":', unknownUserPosts.map(p => ({
          id: p.id,
          username: p.username,
          source_feed: p.source_feed,
          url: p.url,
          created_at: p.created_at
        })));
      }

      // All posts returned are analyzed since we filter server-side
      setPosts(response.posts);
      setPagination(response.pagination);
      // Use total count from pagination, not current page length
      setAnalyzedCount(response.pagination.total);
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSearch = async () => {
    setPagination({ ...pagination, page: 1 });
    await loadPostsWithSearch();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPagination({ ...pagination, page: 1 });
    loadPosts();
  };

  const handleSourceToggle = (source: string) => {
    setSelectedSources(prev =>
      prev.includes(source)
        ? prev.filter(s => s !== source)
        : [...prev, source]
    );
    setPagination({ ...pagination, page: 1 });
  };

  const handleThreatTypeToggle = (threatType: string) => {
    setSelectedThreatTypes(prev =>
      prev.includes(threatType)
        ? prev.filter(t => t !== threatType)
        : [...prev, threatType]
    );
    setPagination({ ...pagination, page: 1 });
  };

  const handleClearAllFilters = () => {
    setThreatLevel('');
    setSearchQuery('');
    setSelectedSources([]);
    setFeedType('');
    setSelectedThreatTypes([]);
    setDateRange({
      startDate: null,
      endDate: null,
      period: 'all'
    });
    setPagination({ ...pagination, page: 1 });
  };

  const hasActiveFilters = threatLevel || searchQuery || selectedSources.length > 0 || feedType || selectedThreatTypes.length > 0 || dateRange.period !== 'all';

  const loadPostsWithSearch = async () => {
    try {
      setRefreshing(true);

      // Prepare date parameters
      const dateParams: any = {};
      if (dateRange.startDate) {
        dateParams.from_date = dateRange.startDate.toISOString();
      }
      if (dateRange.endDate) {
        dateParams.to_date = dateRange.endDate.toISOString();
      }

      // For "all time", don't send date parameters
      const apiDateParams = dateRange.period === 'all' ? {} : dateParams;

      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        analyzed: 'true',
        order_by: 'analyzed_at',
        ...apiDateParams
      };

      if (threatLevel) params.threat_level = threatLevel;
      if (searchQuery) params.search = searchQuery;
      if (selectedSources.length > 0) params.source_feed = selectedSources.join(',');
      if (feedType) params.feed_type = feedType;
      if (selectedThreatTypes.length > 0) params.threat_type = selectedThreatTypes.join(',');

      const response = await api.getPosts(params);

      setPosts(response.posts);
      setPagination(response.pagination);
      setAnalyzedCount(response.pagination.total);
    } catch (error) {
      console.error('Failed to search posts:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatThreatTypes = (threatTypes: string | undefined) => {
    if (!threatTypes) return [];
    try {
      return Array.isArray(threatTypes) ? threatTypes : JSON.parse(threatTypes);
    } catch {
      return [threatTypes];
    }
  };

  const formatList = (listData: string[] | string | undefined) => {
    if (!listData) return [];
    if (Array.isArray(listData)) return listData;
    if (typeof listData === 'string') {
      try {
        return JSON.parse(listData);
      } catch {
        return [listData];
      }
    }
    return [];
  };

  const handleViewAlerts = (postId: string, latestAlertId?: string) => {
    // Navigate to alerts page with the specific post's alerts filtered
    // If there's a latest alert ID, highlight that specific alert
    const params = new URLSearchParams({ post_id: postId });
    if (latestAlertId) {
      params.set('highlight', latestAlertId);
    }
    navigate(`/alerts?${params.toString()}`);
  };

  const ThreatIcon = ({ level }: { level: string }) => {
    const Icon = THREAT_ICONS[level as keyof typeof THREAT_ICONS] || Shield;
    return <Icon className="w-4 h-4" />;
  };

  // Calculate dashboard metrics
  const dashboardMetrics = {
    threatTypeDistribution: (() => {
      if (!posts.length) return [];
      const types = posts.flatMap(p => formatThreatTypes(p.threat_types));
      const counts = types.reduce((acc, type) => {
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      return Object.entries(counts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8);
    })(),

    threatLevelDistribution: (() => {
      if (!posts.length) return [];
      const grouped = posts.reduce((acc, post) => {
        const level = (post.threat_level || 'unknown').toLowerCase();
        acc[level] = (acc[level] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      return Object.entries(grouped).map(([level, count]) => ({
        name: level,
        value: count,
        color: CHART_COLORS[level as keyof typeof CHART_COLORS] || CHART_COLORS.unknown
      }));
    })(),

    topSources: (() => {
      if (!posts.length) return [];
      const sources = posts.reduce((acc, post) => {
        acc[post.username] = (acc[post.username] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      return Object.entries(sources)
        .map(([username, count]) => ({ username, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    })(),

    timelineData: (() => {
      if (!posts.length) return [];
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - i);
        return date.toISOString().split('T')[0];
      }).reverse();

      const postsByDate = posts.reduce((acc, post) => {
        const date = new Date(post.analyzed_at || post.created_at).toISOString().split('T')[0];
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return last7Days.map(date => ({
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        posts: postsByDate[date] || 0
      }));
    })(),

    confidenceDistribution: (() => {
      if (!posts.length) return [];
      const ranges = [
        { name: '90-100%', min: 0.9, max: 1.0 },
        { name: '80-89%', min: 0.8, max: 0.9 },
        { name: '70-79%', min: 0.7, max: 0.8 },
        { name: '<70%', min: 0, max: 0.7 }
      ];

      return ranges.map(range => ({
        name: range.name,
        value: posts.filter(p => p.confidence && p.confidence >= range.min && p.confidence < range.max).length
      }));
    })()
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-gray-900">Analyzed Posts</h1>
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                {analyzedCount} analyzed
              </span>
            </div>
            <p className="text-gray-600 mt-1">Threat intelligence posts with AI analysis and detailed threat assessment</p>
          </div>
          <div className="flex items-center space-x-2">
            <DateFilter
              onChange={setDateRange}
              initialValue={dateRange}
            />
            <button
              onClick={() => setShowDashboard(!showDashboard)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>{showDashboard ? 'Hide' : 'Show'} Dashboard</span>
            </button>
            <button
              onClick={() => {
                loadPosts();
                loadDashboardData();
                loadAnalyzedStats();
              }}
              disabled={refreshing}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {refreshing ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Dashboard Section */}
        {showDashboard && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard
                title="Total Analyzed"
                value={analyzedCount}
                icon={Brain}
                color="purple"
                trend={stats?.trend_data ? `+${stats.trend_data.slice(-1)[0]?.post_count || 0} today` : ''}
              />
              <StatCard
                title="Critical Threats"
                value={analyzedStats?.stats?.critical_threats || 0}
                icon={AlertOctagon}
                color="red"
                percentage={analyzedCount > 0 ? Math.round((analyzedStats?.stats?.critical_threats || 0) / analyzedCount * 100) : 0}
              />
              <StatCard
                title="High Threats"
                value={analyzedStats?.stats?.high_threats || 0}
                icon={AlertTriangle}
                color="orange"
                percentage={analyzedCount > 0 ? Math.round((analyzedStats?.stats?.high_threats || 0) / analyzedCount * 100) : 0}
              />
              <StatCard
                title="Unique Sources"
                value={new Set(posts.map(p => p.username)).size}
                icon={Users}
                color="blue"
                subtitle="contributors"
              />
              <StatCard
                title="Avg Confidence"
                value={posts.length > 0 ? `${Math.round(posts.reduce((acc, p) => acc + (p.confidence || 0), 0) / posts.length * 100)}%` : '0%'}
                icon={Sparkles}
                color="green"
                subtitle="AI confidence"
              />
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Threat Level Distribution */}
              <div className="bg-white p-6 rounded-lg shadow-lg border">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Threat Level Distribution</h3>
                    <p className="text-sm text-gray-500">Analysis of threat severity levels</p>
                  </div>
                  <PieChartIcon className="h-6 w-6 text-purple-500" />
                </div>
                {dashboardMetrics.threatLevelDistribution.length > 0 ? (
                  <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={dashboardMetrics.threatLevelDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {dashboardMetrics.threatLevelDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number, name: string) => [
                            `${value} posts`,
                            name.charAt(0).toUpperCase() + name.slice(1)
                          ]}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No threat data available
                  </div>
                )}
              </div>

              {/* Timeline Chart */}
              <div className="bg-white p-6 rounded-lg shadow-lg border">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Analysis Timeline</h3>
                    <p className="text-sm text-gray-500">Posts analyzed over the last 7 days</p>
                  </div>
                  <TrendingUp className="h-6 w-6 text-blue-500" />
                </div>
                {dashboardMetrics.timelineData.length > 0 ? (
                  <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                      <AreaChart data={dashboardMetrics.timelineData}>
                        <defs>
                          <linearGradient id="colorPosts" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="date" />
                        <YAxis />
                        <CartesianGrid strokeDasharray="3 3" />
                        <Tooltip />
                        <Area type="monotone" dataKey="posts" stroke="#3b82f6" fillOpacity={1} fill="url(#colorPosts)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No timeline data available
                  </div>
                )}
              </div>
            </div>

            {/* Second Row Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Top Threat Types */}
              <div className="bg-white p-6 rounded-lg shadow-lg border">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Top Threat Types</h3>
                    <p className="text-sm text-gray-500">Most common threat categories</p>
                  </div>
                  <Target className="h-6 w-6 text-red-500" />
                </div>
                {dashboardMetrics.threatTypeDistribution.length > 0 ? (
                  <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                      <BarChart data={dashboardMetrics.threatTypeDistribution} layout="horizontal">
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" width={100} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#ef4444" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No threat type data available
                  </div>
                )}
              </div>

              {/* Top Sources */}
              <div className="bg-white p-6 rounded-lg shadow-lg border">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Top Intelligence Sources</h3>
                    <p className="text-sm text-gray-500">Most active contributors</p>
                  </div>
                  <Users className="h-6 w-6 text-green-500" />
                </div>
                <div className="space-y-3">
                  {dashboardMetrics.topSources.length > 0 ? (
                    dashboardMetrics.topSources.map((source, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${index === 0 ? 'bg-yellow-500' :
                              index === 1 ? 'bg-gray-400' :
                                index === 2 ? 'bg-orange-600' :
                                  'bg-blue-500'
                            }`}>
                            {index + 1}
                          </div>
                          <span className="font-medium text-gray-900">@{source.username}</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-600">{source.count} posts</span>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center justify-center h-64 text-gray-500">
                      No source data available
                    </div>
                  )}
                </div>
              </div>

              {/* Confidence Distribution */}
              <div className="bg-white p-6 rounded-lg shadow-lg border">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">AI Confidence Levels</h3>
                    <p className="text-sm text-gray-500">Distribution of analysis confidence</p>
                  </div>
                  <Brain className="h-6 w-6 text-purple-500" />
                </div>
                {dashboardMetrics.confidenceDistribution.some(d => d.value > 0) ? (
                  <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                      <BarChart data={dashboardMetrics.confidenceDistribution}>
                        <XAxis dataKey="name" />
                        <YAxis />
                        <CartesianGrid strokeDasharray="3 3" />
                        <Tooltip />
                        <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No confidence data available
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity Summary */}
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Recent Activity Summary</h3>
                  <p className="text-sm text-gray-500">Latest analyzed threats at a glance</p>
                </div>
                <Activity className="h-6 w-6 text-orange-500" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {posts.slice(0, 8).map((post, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer" onClick={() => setSelectedPostId(post.id)}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${post.threat_level?.toLowerCase() === 'critical' ? 'bg-red-100 text-red-800' :
                          post.threat_level?.toLowerCase() === 'high' ? 'bg-orange-100 text-orange-800' :
                            post.threat_level?.toLowerCase() === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                        }`}>
                        {post.threat_level?.toUpperCase()}
                      </span>
                      {post.confidence && (
                        <span className="text-xs text-gray-500">{Math.round(post.confidence * 100)}%</span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-gray-900 truncate">@{post.username}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {post.analyzed_at ? formatSafeRelativeTime(post.analyzed_at, { addSuffix: true }) : 'Recently'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <hr className="border-gray-200" />

        {/* Enhanced Filters */}
        <div className="bg-white p-6 rounded-lg shadow-sm border space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
              {hasActiveFilters && (
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                  {[threatLevel, ...(selectedSources.length > 0 ? [`${selectedSources.length} sources`] : []), feedType, ...(selectedThreatTypes.length > 0 ? [`${selectedThreatTypes.length} threats`] : [])].filter(Boolean).length} active
                </span>
              )}
            </div>
            {hasActiveFilters && (
              <button
                onClick={handleClearAllFilters}
                className="flex items-center space-x-1 text-gray-500 hover:text-gray-700 text-sm"
              >
                <XCircle className="w-4 h-4" />
                <span>Clear All</span>
              </button>
            )}
          </div>

          {/* Row 1: Search and Threat Level */}
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by post ID or content..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Threat Level Filter */}
            <div className="lg:w-48">
              <label className="block text-sm font-medium text-gray-700 mb-1">Threat Level</label>
              <select
                value={threatLevel}
                onChange={(e) => setThreatLevel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">All Threat Levels</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>
          </div>

          {/* Row 2: Feed Type and Category */}
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Feed Type Filter */}
            <div className="lg:w-48">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Rss className="w-4 h-4 inline mr-1" />
                Source Type
              </label>
              <select
                value={feedType}
                onChange={(e) => setFeedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">All Sources</option>
                <option value="rss">RSS Feeds</option>
                <option value="rss-app">RSS.app</option>
              </select>
            </div>


            {/* Action Buttons */}
            <div className="flex items-end space-x-2">
              <button
                onClick={handleSearch}
                disabled={refreshing}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Search className="w-4 h-4" />
                <span>Search</span>
              </button>
              {hasActiveFilters && (
                <button
                  onClick={handleClearAllFilters}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center space-x-2"
                >
                  <XCircle className="w-4 h-4" />
                  <span>Remove All Filters</span>
                </button>
              )}
            </div>
          </div>

          {/* Row 2b: Threat Type Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                <Tag className="w-4 h-4 inline mr-1" />
                Threat Type Selection
                {selectedThreatTypes.length > 0 && (
                  <span className="ml-2 text-xs text-red-600">({selectedThreatTypes.length} selected)</span>
                )}
              </label>
              {selectedThreatTypes.length > 0 && (
                <button
                  onClick={() => setSelectedThreatTypes([])}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear Threat Types
                </button>
              )}
            </div>

            {/* Multi-select threat types */}
            <ThreatTypesSelector
              threatTypes={availableThreatTypes}
              selectedThreatTypes={selectedThreatTypes}
              onThreatTypeToggle={handleThreatTypeToggle}
            />
          </div>

          {/* Row 3: Source Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                <Settings className="w-4 h-4 inline mr-1" />
                Specific Sources
                {selectedSources.length > 0 && (
                  <span className="ml-2 text-xs text-blue-600">({selectedSources.length} selected)</span>
                )}
              </label>
              {selectedSources.length > 0 && (
                <button
                  onClick={() => setSelectedSources([])}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear Sources
                </button>
              )}
            </div>

            {/* Show feeds based on feedType */}
            {feedType === 'rss-app' ? (
              // Show RSS.app feeds with searchable dropdown
              <RSSAppFeedsSelector
                feeds={rssAppFeeds}
                selectedSources={selectedSources}
                onSourceToggle={handleSourceToggle}
              />
            ) : feedType === 'rss' ? (
              // Show RSS feeds with searchable dropdown
              <RSSFeedsSelector
                feeds={rssFeeds}
                selectedSources={selectedSources}
                onSourceToggle={handleSourceToggle}
              />
            ) : (
              // Show all available sources when no filter specified
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md p-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                  {/* All sources */}
                  {availableSources.map(source => {
                    const isRssApp = source.startsWith('rss-app-');
                    const rssAppFeed = isRssApp ? rssAppFeeds.find(f => f.source_feed === source) : null;

                    return (
                      <label key={source} className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedSources.includes(source)}
                          onChange={() => handleSourceToggle(source)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 truncate flex-1" title={source}>
                          {isRssApp ? (
                            <>
                              <span className="text-xs text-purple-600 bg-purple-100 px-1 rounded mr-1">RSS.app</span>
                              {rssAppFeed?.name || source.replace('rss-app-', '')}
                            </>
                          ) : (
                            source
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {availableSources.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No sources available. Posts will appear here once they are analyzed.</p>
                )}
              </div>
            )}
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="pt-4 border-t border-gray-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Active filters:</span>
                {threatLevel && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Threat: {threatLevel}
                    <button onClick={() => setThreatLevel('')} className="ml-1 text-red-600 hover:text-red-800">
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {feedType && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Type: {feedType === 'rss' ? 'RSS Feeds' : 'RSS.app'}
                    <button onClick={() => setFeedType('')} className="ml-1 text-blue-600 hover:text-blue-800">
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {selectedThreatTypes.map(threatType => (
                  <span key={threatType} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Threat: {availableThreatTypes.find(t => t.name === threatType)?.display_name || threatType}
                    <button onClick={() => handleThreatTypeToggle(threatType)} className="ml-1 text-red-600 hover:text-red-800">
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {selectedSources.map(source => (
                  <span key={source} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {source.length > 20 ? source.substring(0, 20) + '...' : source}
                    <button onClick={() => handleSourceToggle(source)} className="ml-1 text-purple-600 hover:text-purple-800">
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {searchQuery && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Search: "{searchQuery.length > 20 ? searchQuery.substring(0, 20) + '...' : searchQuery}"
                    <button onClick={handleClearSearch} className="ml-1 text-gray-600 hover:text-gray-800">
                      <XCircle className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Posts List */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
              <Brain className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No analyzed posts found</h3>
              <p className="text-gray-600">Posts will appear here once they are processed and analyzed.</p>
            </div>
          ) : (
            posts.map((post) => {
              const isExpanded = expandedPost === (post.post_id || post.id);
              const threatTypes = formatThreatTypes(post.threat_types);
              const ttps = formatList(post.ttps);
              const iocs = formatList(post.iocs);
              const recommendations = formatList(post.recommendations);

              return (
                <div
                  key={post.id}
                  id={`post-${post.id}`}
                  className={`bg-white rounded-lg shadow-sm border overflow-hidden ${searchParams.get('highlight') === post.id ? 'ring-2 ring-purple-500 ring-opacity-50' : ''
                    }`}
                >
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center flex-wrap gap-2">
                        <div className={`px-2 py-1 rounded-full border text-xs font-medium ${THREAT_COLORS[post.threat_level as keyof typeof THREAT_COLORS]}`}>
                          <div className="flex items-center space-x-1">
                            <ThreatIcon level={post.threat_level} />
                            <span>{post.threat_level.toUpperCase()}</span>
                          </div>
                        </div>

                        {post.confidence && (
                          <div className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            {Math.round(post.confidence * 100)}% confidence
                          </div>
                        )}

                        {/* Content Type Indicators */}
                        <div className="flex items-center gap-2">
                          <div className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            <span>Text</span>
                          </div>

                          {post.has_images && (
                            <div className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium flex items-center gap-1">
                              <ImageIcon className="w-3 h-3" />
                              <span>Image</span>
                            </div>
                          )}

                          {post.website_crawls_count !== undefined && post.website_crawls_count > 0 && (
                            <div className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              <span>{post.website_crawls_count} crawls</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span>analyzed {formatSafeRelativeTime(post.analyzed_at || post.created_at, { addSuffix: true })}</span>
                      </div>
                    </div>

                    {/* Content Preview */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4 text-gray-500" />
                          <span className="font-medium text-gray-900">{post.username}</span>
                        </div>

                        {/* Prominent Source Badge in Header */}
                        <SourceBadge source_feed={post.source_feed} username={post.username} size="sm" />
                      </div>

                      <div className="flex items-center space-x-2">{/* This div will contain the Post ID and other badges */}

                        {/* Post ID - Clickable */}
                        <span
                          className="text-xs font-mono text-blue-600 bg-blue-50 px-2 py-0.5 rounded cursor-pointer hover:bg-blue-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPostId(post.post_id || post.id);
                          }}
                          title="Click to view post details"
                        >
                          Post: {formatPostIdForDisplay(post.post_id || post.id)}
                        </span>

                        {/* Alert ID - Clickable if exists */}
                        {post.latest_alert_id && (
                          <span
                            className="text-xs font-mono text-red-600 bg-red-50 px-2 py-0.5 rounded cursor-pointer hover:bg-red-100 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewAlerts(post.post_id || post.id, post.latest_alert_id);
                            }}
                            title="Click to view alert details"
                          >
                            Alert: {post.latest_alert_id}
                          </span>
                        )}

                        {/* Analysis ID */}
                        {post.analysis_id && (
                          <span className="text-xs font-mono text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                            Analysis: {post.analysis_id}
                          </span>
                        )}
                      </div>

                      <p className="text-gray-700 line-clamp-3">
                        {post.content.length > 200 && !isExpanded
                          ? `${post.content.substring(0, 200)}...`
                          : post.content
                        }
                      </p>
                    </div>

                    {/* AI Analysis Summary */}
                    {post.summary && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Brain className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-900">AI Analysis</span>
                          </div>
                          {(post.ai_analysis?.ai_provider || post.enhanced_analysis?.ai_provider) && (
                            <div className="flex items-center space-x-2 text-xs">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                                {post.ai_analysis?.ai_provider || post.enhanced_analysis?.ai_provider}
                              </span>
                              {(post.ai_analysis?.ai_model || post.enhanced_analysis?.ai_model) && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                                  {(post.ai_analysis?.ai_model || post.enhanced_analysis?.ai_model)?.split('/').pop() || 'default'}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-blue-800">{post.summary}</p>
                      </div>
                    )}

                    {/* Threat Types */}
                    {threatTypes.length > 0 && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {threatTypes.map((type, index) => (
                            <span key={index} className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                              <Target className="w-3 h-3 inline mr-1" />
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Expand/Collapse Button */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {post.url && (
                          <a
                            href={post.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <ExternalLink className="w-4 h-4" />
                            <span>View Original</span>
                          </a>
                        )}

                        {/* View Alert Link */}
                        {post.alert_count && post.alert_count > 0 && (
                          <button
                            onClick={() => handleViewAlerts(post.post_id || post.id, post.latest_alert_id)}
                            className="flex items-center space-x-1 text-orange-600 hover:text-orange-800 text-sm font-medium"
                          >
                            <Bell className="w-4 h-4" />
                            <span>View Alert{post.alert_count > 1 ? 's' : ''} ({post.alert_count})</span>
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        )}

                        {post.analyzed_at && (
                          <div className="flex items-center space-x-1 text-gray-500 text-sm">
                            <Clock className="w-4 h-4" />
                            <span>Analyzed {formatSafeRelativeTime(post.analyzed_at, { addSuffix: true })}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            console.log('Post analysis_id:', post.analysis_id, 'Post ID:', post.id);
                            // For now, use post ID as fallback since the new analysis endpoints handle this
                            navigate(`/analysis/${post.analysis_id || post.id}`);
                          }}
                          className="flex items-center space-x-1 text-purple-600 hover:text-purple-800 text-sm"
                        >
                          <Maximize2 className="w-4 h-4" />
                          <span>View Full Details</span>
                        </button>

                        {(ttps.length > 0 || iocs.length > 0 || recommendations.length > 0 || post.analyzed_steps) && (
                          <button
                            onClick={() => setExpandedPost(isExpanded ? null : post.post_id || post.id)}
                            className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            <span>{isExpanded ? 'Hide Analysis' : 'Show Analysis'}</span>
                            <ChevronDown className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="mt-6 pt-6 border-t space-y-6">
                        {/* Analysis Steps */}
                        {post.analyzed_steps && (
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                            <div className="flex items-center space-x-2 mb-3">
                              <Activity className="w-5 h-5 text-blue-600" />
                              <h4 className="text-sm font-semibold text-blue-900">AI Analysis Steps</h4>
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-xs">
                              <div className="flex items-center space-x-2">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <span className="text-gray-600">Topic Relevance:</span>
                                <span className="font-medium">{(post.analyzed_steps.topic_relevance * 100).toFixed(1)}%</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Zap className="w-4 h-4 text-yellow-500" />
                                <span className="text-gray-600">AI Provider:</span>
                                <span className="font-medium">{post.ai_provider || post.ai_analysis?.ai_provider || post.enhanced_analysis?.ai_provider || 'unknown'}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Brain className="w-4 h-4 text-blue-500" />
                                <span className="text-gray-600">AI Model:</span>
                                <span className="font-medium font-mono text-xs">
                                  {(post.ai_model || post.ai_analysis?.ai_model || post.enhanced_analysis?.ai_model || post.analyzed_steps?.ai_model_used || 'unknown')
                                    .split('/').pop()?.split('-').slice(0, 2).join('-') || 'unknown'}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Target className="w-4 h-4 text-red-500" />
                                <span className="text-gray-600">Threat Assessment:</span>
                                <span className="font-medium capitalize">{post.analyzed_steps.threat_assessment}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <span className="text-gray-600">Confidence:</span>
                                <span className="font-medium">{(post.analyzed_steps.confidence_score * 100).toFixed(1)}%</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* TTPs */}
                        {ttps.length > 0 && (
                          <div>
                            <div className="flex items-center space-x-2 mb-3">
                              <Shield className="w-4 h-4 text-purple-600" />
                              <h4 className="text-sm font-medium text-gray-900">MITRE ATT&CK TTPs</h4>
                              <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">{ttps.length} found</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {ttps.map((ttp, index) => (
                                <span key={index} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-mono hover:bg-purple-200 transition-colors">
                                  {ttp}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* IOCs */}
                        {iocs.length > 0 && (
                          <div>
                            <div className="flex items-center space-x-2 mb-3">
                              <AlertTriangle className="w-4 h-4 text-orange-600" />
                              <h4 className="text-sm font-medium text-gray-900">Indicators of Compromise</h4>
                              <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">{iocs.length} detected</span>
                            </div>
                            <div className="space-y-2">
                              {iocs.map((ioc, index) => (
                                <div key={index} className="text-xs font-mono bg-orange-50 border border-orange-200 p-3 rounded-lg">
                                  <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                                    <span className="text-orange-800 font-medium">{ioc}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Recommendations */}
                        {recommendations.length > 0 && (
                          <div>
                            <div className="flex items-center space-x-2 mb-3">
                              <FileText className="w-4 h-4 text-green-600" />
                              <h4 className="text-sm font-medium text-gray-900">Security Recommendations</h4>
                              <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">{recommendations.length} actions</span>
                            </div>
                            <div className="space-y-2">
                              {recommendations.map((rec, index) => (
                                <div key={index} className="text-sm text-gray-700 flex items-start bg-green-50 border border-green-200 p-3 rounded-lg">
                                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                                  <span>{rec}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Analysis Summary */}
                        {post.summary && (
                          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                            <div className="flex items-center space-x-2 mb-2">
                              <Brain className="w-4 h-4 text-gray-600" />
                              <h4 className="text-sm font-medium text-gray-900">Detailed Analysis Summary</h4>
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed">{post.summary}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white rounded-lg shadow">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  color: 'purple' | 'red' | 'orange' | 'blue' | 'green';
  trend?: string;
  percentage?: number;
  subtitle?: string;
}

function StatCard({ title, value, icon: Icon, color, trend, percentage, subtitle }: StatCardProps) {
  const colorClasses = {
    purple: 'bg-purple-500 text-white',
    red: 'bg-red-500 text-white',
    orange: 'bg-orange-500 text-white',
    blue: 'bg-blue-500 text-white',
    green: 'bg-green-500 text-white'
  };

  const bgClasses = {
    purple: 'bg-purple-50',
    red: 'bg-red-50',
    orange: 'bg-orange-50',
    blue: 'bg-blue-50',
    green: 'bg-green-50'
  };

  return (
    <div className="bg-white overflow-hidden shadow-lg rounded-lg border">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className={`p-3 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
              <Icon className="h-6 w-6" />
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">
                {title}
              </dt>
              <dd className="flex items-baseline">
                <div className="text-2xl font-bold text-gray-900">
                  {value}
                </div>
                {percentage !== undefined && (
                  <span className="ml-2 text-sm font-medium text-gray-500">
                    ({percentage}%)
                  </span>
                )}
              </dd>
              {(trend || subtitle) && (
                <dd className="mt-1">
                  {trend && (
                    <span className="text-xs font-medium text-green-600">
                      {trend}
                    </span>
                  )}
                  {subtitle && (
                    <span className="text-xs text-gray-500">
                      {subtitle}
                    </span>
                  )}
                </dd>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}